import { Component, OnDestroy, OnInit } from '@angular/core';
import { SurveyQuestionnaireService } from '../survey-questionnaire.service';
import { Subject, map, takeUntil } from 'rxjs';
import {
  SurveyQuestionGroup,
  SurveyQuestionGroupValidation,
  SurveyQuestionType,
  SurveyNavigationType,
  SurveyQuestionInactiveDisplayType,
} from '../models/survey.model';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BrightsideUiModule, SurveySelectorData, SurveySelectorType, type BrightsideCurrencyMaskOptions } from '@brightside/brightside-ui';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FirebaseAnalyticsService } from '@brightside-web/desktop/data-access/shared';
import { SharedDesktopModule } from '@brightside-web/shared/desktop';
import { MicroUiMoleculeModule } from '@micro-ui/molecule';
import { CommonModule, NgClass, NgSwitch, UpperCasePipe } from '@angular/common';

@Component({
    selector: 'brightside-web-survey-view',
    templateUrl: './survey-view.component.html',
    styleUrls: ['./survey-view.component.scss'],
    imports: [
        CommonModule,
        SharedDesktopModule,
        BrightsideUiModule,
        MicroUiMoleculeModule,
        TranslateModule,
        ReactiveFormsModule,
        NgClass,
        UpperCasePipe,
        NgSwitch,
    ]
})
export class SurveyViewComponent implements OnInit, OnDestroy {
  surveyId: string | undefined;
  surveyName: string | undefined;
  surveyType: string;
  surveyCurrentQuestionGroup: SurveyQuestionGroup | undefined;
  surveyCurrentQuestionsData: any[] | undefined;
  showCtaButton: boolean | undefined;
  SurveyQuestionType = SurveyQuestionType;
  SurveySelectorType = SurveySelectorType;
  readonly surveyCurrencyMask: BrightsideCurrencyMaskOptions = {
    prefix: '$',
    align: 'left',
    nullable: true,
    allowNegative: false,
    precision: 2,
  };
  ctaButtonDisabled: boolean;
  processing: boolean;

  isOnboarding = this._surveyQuetionnaireService.isOnboarding;
  protected _currentQuestionGroupIndex = -1;
  private _unsub$: Subject<void> = new Subject<void>();
  private _surveyQuestionsGroup: SurveyQuestionGroup[] | undefined;
  private _allQuestionsActiveIds: string[] = [];

  constructor(
    private _surveyQuetionnaireService: SurveyQuestionnaireService,
    private _translatePipe: TranslatePipe,
    private _router: Router,
    private _route: ActivatedRoute,
    private _analytics: FirebaseAnalyticsService
  ) {}

  ngOnInit(): void {
    this._surveyQuetionnaireService.surveyData$
      .pipe(
        map((surveyData) => {
          this.surveyName = surveyData?.name;
          this.surveyId = surveyData?.surveyId;
          this.surveyType = surveyData?.type || '';
          this._surveyQuestionsGroup = surveyData?.questionGroups;
          this.setIndexToTheLastUnansweredQuestion();
          this.moveToNextQuestion();
          this.validateCurrentQuestionGroup();
        }),
        takeUntil(this._unsub$)
      )
      .subscribe();
  }

  backToPreviousStep(): void {
    if (this._currentQuestionGroupIndex <= 0) {
      this._router.navigate(['../intro'], { relativeTo: this._route });
    } else {
      this.moveToPreviousQuestion();
      this.setCurrentQuestionActiveIds();
      this.validateQuestionGroupActivation(SurveyNavigationType.BACKWARD);
      this.validateCurrentQuestionGroup();
    }
  }

  continueToNextStep(updateToServer = true): void {
    if (
      this._surveyQuestionsGroup &&
      this._currentQuestionGroupIndex === this._surveyQuestionsGroup.length - 1 &&
      updateToServer
    ) {
      this.processing = true;
      this.completeSurvey();
    } else {
      this.setCurrentQuestionActiveIds();
      this.moveToNextQuestion();
      this.validateQuestionGroupActivation(SurveyNavigationType.FORWARD);
      this.validateCurrentQuestionGroup();
      if (updateToServer) this.updateSurveyData();
    }
  }

  skipQuestion(): void {
    if (this.surveyCurrentQuestionGroup) {
      this.surveyCurrentQuestionGroup.skipped = true;
      this.continueToNextStep();
    }
  }

  ngOnDestroy(): void {
    this._unsub$.next();
  }

  logAnswerAnalytics(continueToNextStep?: boolean): void {
    this._analytics.logEvent('survey tapped', {
      custom: {
        type: this.surveyType.split('_').join(' '),
        value: this.surveyCurrentQuestionGroup?.id.split('_').join(' '),
      },
    });

    if (continueToNextStep) {
      setTimeout(() => { this.continueToNextStep(); }, 100);
    }
  }

  private setIndexToTheLastUnansweredQuestion(): void {
    this.setCurrentQuestionActiveIds();
    if (!this._allQuestionsActiveIds.length) {
      this._currentQuestionGroupIndex = -1;
      return;
    }

    if (this._surveyQuestionsGroup) {
      for (let qg of this._surveyQuestionsGroup) {
        if (
          !qg.questions.find((q) => this._allQuestionsActiveIds.includes(q.questionId)) &&
          (!qg.activateWhen.length || qg.activateWhen.some((item) => this._allQuestionsActiveIds.includes(item)))
        ) {
          this._currentQuestionGroupIndex = (this._surveyQuestionsGroup?.indexOf(qg) || 1) - 1;
          break;
        }
      }
    }
  }

  private completeSurvey(): void {
    if (this._surveyQuetionnaireService.isOnboarding) {
      this._surveyQuetionnaireService.backToHomeCalled('done');
    } else {
      this._router.navigate(['../thank-you'], { relativeTo: this._route });
    }
    this.updateSurveyData(true);
  }

  private prepareQuestionData(): any[] | undefined {
    const currentQuestionGroup = this.surveyCurrentQuestionGroup?.questions.map((q) => {
      let options: SurveySelectorData[];
      if (
        [
          SurveyQuestionType.FORM_CURRENCY,
          SurveyQuestionType.FORM_NUMBER,
          SurveyQuestionType.FORM_TEXT,
          SurveyQuestionType.FORM_TEXT_AREA,
        ].includes(q.type)
      ) {
        if (q.value) this.validateCurrentQuestionGroup();

        return {
          title: q.title,
          placeholder: q.placeholder,
          type: q.type,
          formControl: new FormControl(q.value || '', []),
          questionId: q.questionId,
          disabled: false,
          hidden: false,
        };
      }

      let formGroup: FormGroup;

      if (q.type === SurveyQuestionType.INSTANT) {
        const value = q.options.find((o) => o.selected)?.optionId;
        formGroup = new FormGroup({ instantSelection: new FormControl(value || '') });
      } else if (q.type === SurveyQuestionType.SINGLE) {
        const value = q.options.find((o) => o.selected)?.optionId;
        formGroup = new FormGroup({ singleSelection: new FormControl(value || '') });
      } else if (q.type === SurveyQuestionType.FORM_TEXT_AREA) {
        const value = q.options.find((o) => o.selected)?.optionId;
        formGroup = new FormGroup({ textArea: new FormControl(value || '') });
      } else {
        formGroup = new FormGroup({ multiSelection: new FormGroup({}) });
      }

      options = q.options.map((o) => ({
        title: this._translatePipe.transform(o.title),
        value: o.optionId,
        checked: o.selected,
        disabled: false,
        hidden: false,
      }));

      return {
        data: options as SurveySelectorData[],
        type: q.type,
        formGroup,
        questionId: q.questionId,
        title: q.title,
        formControl: new FormControl('', []),
        disabled: false,
        hidden: false,
      };
    });

    currentQuestionGroup?.forEach((q) => {
      if ([SurveyQuestionType.MULTI, SurveyQuestionType.INSTANT, SurveyQuestionType.SINGLE].includes(q.type)) {
        q.formGroup?.valueChanges.pipe(takeUntil(this._unsub$)).subscribe(() => {
          this.updateQuestionResponseToSurveyData(q.questionId, q.formGroup);
          this.setCurrentQuestionActiveIds();
          this.validateQuestionGroupActivation(SurveyNavigationType.FORWARD, true);
        });
      } else {
        q.formControl.valueChanges
          .pipe(takeUntil(this._unsub$))
          .subscribe(() => this.updateQuestionResponseToSurveyData(q.questionId));
      }
    });

    return currentQuestionGroup;
  }

  private moveToNextQuestion(): void {
    this._currentQuestionGroupIndex++;
    this.setCurrentQuestionGroupData();
  }

  private moveToPreviousQuestion(): void {
    this._currentQuestionGroupIndex--;
    this.setCurrentQuestionGroupData();
  }

  private setCurrentQuestionGroupData(): void {
    this.surveyCurrentQuestionGroup = this._surveyQuestionsGroup && this._surveyQuestionsGroup[this._currentQuestionGroupIndex];
    this.showCtaButton = !this.surveyCurrentQuestionGroup?.questions.every((q) => q.type === SurveyQuestionType.INSTANT);
    this.ctaButtonDisabled = true;
    this.setCurrentQuestionFromSurveyState();
    this.surveyCurrentQuestionsData = this.prepareQuestionData();
  }

  private updateQuestionResponseToSurveyData(questonId: string, formGroup: FormGroup = new FormGroup({})): void {
    this.surveyCurrentQuestionGroup?.questions.forEach((q) => {
      if (q.questionId === questonId) {
        if (q.type === SurveyQuestionType.INSTANT) {
          const selectedValue = formGroup.value.instantSelection;
          q.options.forEach((o) => (o.selected = o.optionId === selectedValue));
        }
        if (q.type === SurveyQuestionType.MULTI) {
          const selectedValues = formGroup.value.multiSelection;
          q.options.forEach((o) => (o.selected = selectedValues[o.optionId]));
        }
        if (q.type === SurveyQuestionType.SINGLE) {
          const selectedValue = formGroup.value.singleSelection;
          q.options.forEach((o) => (o.selected = o.optionId === selectedValue));
        }
        if (
          [
            SurveyQuestionType.FORM_CURRENCY,
            SurveyQuestionType.FORM_NUMBER,
            SurveyQuestionType.FORM_TEXT,
            SurveyQuestionType.FORM_TEXT_AREA,
          ].includes(q.type)
        ) {
          const matchingQuestion = this.surveyCurrentQuestionsData?.find((qd) => qd.questionId === q.questionId);
          q.value = `${matchingQuestion.formControl.value}`;
        }
      }
    });
    setTimeout(() => {
      this.validateCurrentQuestionGroup();
    }, 0);
  }

  private validateCurrentQuestionGroup(): void {
    const questionGroup = this.surveyCurrentQuestionGroup as SurveyQuestionGroup;

    if (questionGroup?.validation === SurveyQuestionGroupValidation.SINGLE) {
      this.ctaButtonDisabled = !questionGroup.questions.some((q) => {
        const matchingQuestionData = this.surveyCurrentQuestionsData?.find((qd) => qd.questionId === q.questionId);
        if ([SurveyQuestionType.MULTI, SurveyQuestionType.SINGLE, SurveyQuestionType.INSTANT].includes(q.type)) {
          return q.options.some((o) => o.selected);
        } else if (q.type === SurveyQuestionType.FORM_TEXT_AREA) {
          return matchingQuestionData?.formControl.valid;
        } else {
          return !!q.value;
        }
      });
    } else if (questionGroup?.validation === SurveyQuestionGroupValidation.ALL) {
      this.ctaButtonDisabled = !questionGroup.questions.every((q) => {
        const matchingQuestionData = this.surveyCurrentQuestionsData?.find((qd) => qd.questionId === q.questionId);
        if (matchingQuestionData?.disabled || matchingQuestionData?.hidden) {
          return true;
        }
        if ([SurveyQuestionType.MULTI, SurveyQuestionType.SINGLE, SurveyQuestionType.INSTANT].includes(q.type)) {
          return q.options.some((o) => o.selected);
        } else if (q.type === SurveyQuestionType.FORM_TEXT_AREA) {
          return matchingQuestionData?.formControl.valid;
        } else {
          return !!q.value;
        }
      });
    }
  }

  private setCurrentQuestionActiveIds(): void {
    this._allQuestionsActiveIds = [];
    this._surveyQuestionsGroup?.forEach((qg) => {
      let activeIds: string[] = [];
      qg?.questions.forEach((q) => {
        if (q.type === SurveyQuestionType.INSTANT) {
          const matchingOption = q.options.find((o) => o.selected);
          if (matchingOption) {
            activeIds.push(matchingOption.optionId);
            if (!activeIds.includes(q.questionId)) activeIds.push(q.questionId);
          }
        }
        if (q.type === SurveyQuestionType.MULTI) {
          q.options.forEach((o) => {
            if (o.selected) {
              activeIds.push(o.optionId);
              if (!activeIds.includes(q.questionId)) activeIds.push(q.questionId);
            }
          });
        }
        if (q.type === SurveyQuestionType.SINGLE) {
          const matchingOption = q.options.find((o) => o.selected);
          if (matchingOption) {
            activeIds.push(matchingOption.optionId);
            if (!activeIds.includes(q.questionId)) activeIds.push(q.questionId);
          }
        }
        if ([SurveyQuestionType.FORM_CURRENCY, SurveyQuestionType.FORM_NUMBER, SurveyQuestionType.FORM_TEXT].includes(q.type)) {
          if (!!q.value && !activeIds.includes(q.questionId)) {
            activeIds.push(q.questionId);
          }
        }
        if (activeIds.length) {
          activeIds.forEach((id) => {
            if (!this._allQuestionsActiveIds.includes(id)) {
              this._allQuestionsActiveIds.push(id);
            }
          });
        } else {
          this._allQuestionsActiveIds = this._allQuestionsActiveIds.filter((id) => q.questionId !== id);
        }
      });
    });
  }

  private setCurrentQuestionFromSurveyState(): void {
    const matchingQuestionGroup = this._surveyQuestionsGroup?.find((qg) => qg.id === this.surveyCurrentQuestionGroup?.id);
    if (matchingQuestionGroup) {
      this.surveyCurrentQuestionGroup = { ...matchingQuestionGroup } as SurveyQuestionGroup;
    }
  }

  private validateQuestionGroupActivation(
    navigationType: SurveyNavigationType = SurveyNavigationType.FORWARD,
    skipQuestionGroup = false
  ): void {
    const currentQuestionGroup = this.surveyCurrentQuestionGroup as SurveyQuestionGroup;
    if (currentQuestionGroup.skipped) {
      if (navigationType === SurveyNavigationType.FORWARD) this.continueToNextStep(false);
      else this.backToPreviousStep();
      return;
    }

    if (currentQuestionGroup?.activateWhen.length && !skipQuestionGroup) {
      const canActivate = this._allQuestionsActiveIds.some((id) => currentQuestionGroup.activateWhen.includes(id));
      if (!canActivate) {
        if (navigationType === SurveyNavigationType.FORWARD) this.continueToNextStep(false);
        else this.backToPreviousStep();
        return;
      }
    }

    currentQuestionGroup?.questions.forEach((q) => {
      if (q.activateWhen.length) {
        const canActivate = this._allQuestionsActiveIds.some((id) => q.activateWhen.includes(id));
        this.surveyCurrentQuestionsData?.forEach((qd) => {
          if (qd.questionId === q.questionId) {
            if (q.inactiveDisplayType === SurveyQuestionInactiveDisplayType.DISABLED) {
              qd.disabled = !canActivate;
            } else {
              qd.hidden = !canActivate;
            }

            if (!canActivate) {
              q.options?.forEach((o) => (o.selected = false));
              const data = qd.data;
              if (data) {
                data?.forEach((d: any) => (d.checked = false));
                qd.data = [...data];
              }
              qd.formGroup?.reset({}, { emitEvent: false });
              qd.formControl?.reset('', { emitEvent: false });
            }
          }
        });
      }

      q.options?.forEach((o) => {
        if (o.activateWhen.length) {
          const canActivate = this._allQuestionsActiveIds.some((id) => o.activateWhen.includes(id));
          const matchingQuestion = this.surveyCurrentQuestionsData?.find((qd) => qd.questionId === q.questionId);
          const matchingOption = matchingQuestion.data.find((d: any) => d.value === o.optionId);
          if (o.inactiveDisplayType === SurveyQuestionInactiveDisplayType.DISABLED) {
            matchingOption.disabled = !canActivate;
            if (!canActivate) {
              matchingOption.checked = false;
              o.selected = false;
            }
          } else {
            matchingOption.hidden = !canActivate;
            if (!canActivate) {
              matchingOption.checked = false;
              o.selected = false;
            }
          }
        }
      });
    });

    this.validateCurrentQuestionGroup();
  }

  private updateSurveyData(isComplete = false): void {
    this._surveyQuetionnaireService.updateSurveyData(
      this._surveyQuetionnaireService.surveyType,
      this.surveyId as string,
      this._surveyQuestionsGroup as SurveyQuestionGroup[],
      isComplete
    );
  }
}
